import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Collaboration, Room } from "src/app/models/Chat.v2";
import Message from "src/app/models/Message";

type ChatType = "allChat" | "myChat";

interface MessageState {
  messagesByRoom: {
    [key: string]: {
      messages: Message[];
      hasMore: boolean;
      isLoading: boolean;
      lastVisible?: any;
    };
  };
}
interface ChatListState {
  collaborationOrder: string[];
  collaborationsById: Record<string, Collaboration>;
  hasMore: boolean;
  isLoading: boolean;
  unreadCount: number;
  selectedCollaborationId: string | null;
  selectedRoomId: string | null;
  newestKnownLastInteractionAt?: any;
  oldestVisibleLastInteractionAt?: any;
  lastFetchedAt?: number;
}

interface ChatSliceState extends MessageState {
  allChat: ChatListState;
  myChat: ChatListState;
  activeTab: ChatType;
  filter: string;
}

const createEmptyChatState = (): ChatListState => ({
  collaborationOrder: [],
  collaborationsById: {},
  hasMore: true,
  isLoading: false,
  unreadCount: 0,
  selectedCollaborationId: null,
  selectedRoomId: null,
});

const initialState: ChatSliceState = {
  allChat: createEmptyChatState(),
  myChat: createEmptyChatState(),
  activeTab: "myChat",
  filter: "",
  messagesByRoom: {},
};

const chatSlice = createSlice({
  name: "ChatV2",
  initialState,
  reducers: {
    // 🔄 Switch tab
    setActiveTab(state, action: PayloadAction<ChatType>) {
      state.activeTab = action.payload;
    },

    // 🔍 Filter
    setFilter(state, action: PayloadAction<string>) {
      state.filter = action.payload;
    },

    // ⏳ Loading
    setLoading(
      state,
      action: PayloadAction<{ chatType: ChatType; isLoading: boolean }>
    ) {
      const { chatType, isLoading } = action.payload;
      state[chatType].isLoading = isLoading;
    },

    // 📦 hasMore
    setHasMore(
      state,
      action: PayloadAction<{ chatType: ChatType; hasMore: boolean }>
    ) {
      const { chatType, hasMore } = action.payload;
      state[chatType].hasMore = hasMore;
    },

    setChatListCursor(
      state,
      action: PayloadAction<{
        chatType: ChatType;
        newestKnownLastInteractionAt?: any;
        oldestVisibleLastInteractionAt?: any;
        lastFetchedAt?: number;
      }>
    ) {
      const {
        chatType,
        newestKnownLastInteractionAt,
        oldestVisibleLastInteractionAt,
        lastFetchedAt,
      } = action.payload;
      const chatState = state[chatType];

      if (
        newestKnownLastInteractionAt &&
        isAfterTimestamp(
          newestKnownLastInteractionAt,
          chatState.newestKnownLastInteractionAt
        )
      ) {
        chatState.newestKnownLastInteractionAt = newestKnownLastInteractionAt;
      }
      if (
        oldestVisibleLastInteractionAt &&
        isBeforeTimestamp(
          oldestVisibleLastInteractionAt,
          chatState.oldestVisibleLastInteractionAt
        )
      ) {
        chatState.oldestVisibleLastInteractionAt = oldestVisibleLastInteractionAt;
      }
      if (lastFetchedAt) {
        chatState.lastFetchedAt = lastFetchedAt;
      }
    },

    // ✅ Update or Add 1
    updateCollaboration(
      state,
      action: PayloadAction<{ chatType: ChatType; data: Collaboration }>
    ) {
      const { chatType, data } = action.payload;
      const chatState = state[chatType];

      chatState.collaborationsById[data.id] = {
        ...chatState.collaborationsById[data.id],
        ...data,
      };

      chatState.collaborationOrder = chatState.collaborationOrder.filter(
        (id) => id !== data.id
      );

      chatState.collaborationOrder.unshift(data.id);

      chatState.collaborationOrder = sortCollaborationIdsByLastInteraction(
        chatState.collaborationsById,
        [...new Set(chatState.collaborationOrder)]
      );

      chatState.unreadCount = calculateTotalUnread(
        chatState.collaborationsById
      );
    },

    // ➕ Add banyak (overwrite)
    setCollaborations(
      state,
      action: PayloadAction<{ chatType: ChatType; data: Collaboration[] }>
    ) {
      const { chatType, data } = action.payload;
      const chatState = state[chatType];

      for (const collab of data) {
        chatState.collaborationsById[collab.id] = collab;
      }

      const ids = data.map((c) => c.id);
      chatState.collaborationOrder = sortCollaborationIdsByLastInteraction(
        chatState.collaborationsById,
        ids
      );

      chatState.unreadCount = calculateTotalUnread(
        chatState.collaborationsById
      );
    },

    // ➕ Add banyak (append)
    appendCollaborations(
      state,
      action: PayloadAction<{ chatType: ChatType; data: Collaboration[] }>
    ) {
      const { chatType, data } = action.payload;
      const chatState = state[chatType];

      for (const collab of data) {
        chatState.collaborationsById[collab.id] = collab;
      }

      const combined = [
        ...chatState.collaborationOrder,
        ...data.map((c) => c.id),
      ];
      const uniqueIds = [...new Set(combined)];

      chatState.collaborationOrder = sortCollaborationIdsByLastInteraction(
        chatState.collaborationsById,
        uniqueIds
      );

      chatState.unreadCount = calculateTotalUnread(
        chatState.collaborationsById
      );
    },

    // ❌ Remove
    removeCollaboration(
      state,
      action: PayloadAction<{ chatType: ChatType; id: string }>
    ) {
      const { chatType, id } = action.payload;
      const chatState = state[chatType];
      delete chatState.collaborationsById[id];
      chatState.collaborationOrder = chatState.collaborationOrder.filter(
        (cid) => cid !== id
      );

      chatState.unreadCount = calculateTotalUnread(
        chatState.collaborationsById
      );
    },

    setSelectedCollaboration(
      state,
      action: PayloadAction<
        NonNullable<{ chatType: ChatType; id: string | null }>
      >
    ) {
      const { chatType, id } = action.payload;
      const chatState = state[chatType];
      chatState.selectedCollaborationId = id;
    },

    setSelectedRoom(
      state,
      action: PayloadAction<
        NonNullable<{ chatType: ChatType; id: string | null }>
      >
    ) {
      const { chatType, id } = action.payload;
      const chatState = state[chatType];
      chatState.selectedRoomId = id;
    },

    setCollaborationRooms(
      state,
      action: PayloadAction<{
        chatType: ChatType;
        collaborationId: string;
        rooms: Room[];
      }>
    ) {
      const { chatType, collaborationId, rooms } = action.payload;
      const collab = state[chatType].collaborationsById[collaborationId];
      if (!collab) return;
      collab.roomsModel = rooms;
    },

    resetRoomUnreadCount(
      state,
      action: PayloadAction<{
        chatType: ChatType;
        collaborationId: string;
        roomId: string;
      }>
    ) {
      const { chatType, collaborationId, roomId } = action.payload;
      const collab = state[chatType].collaborationsById[collaborationId];
      if (!collab) return;

      const room = collab.roomsModel?.find((item) => item.id === roomId);
      if (!room) return;

      const roomUnreadCount = Math.max(0, room.unreadCount ?? 0);
      room.unreadCount = 0;
      collab.unreadCount = Math.max(
        0,
        (collab.unreadCount ?? 0) - roomUnreadCount
      );
      state[chatType].unreadCount = calculateTotalUnread(
        state[chatType].collaborationsById
      );
    },

    clearSelectedChat(
      state,
      action: PayloadAction<NonNullable<{ chatType: ChatType }>>
    ) {
      const { chatType } = action.payload;
      const chatState = state[chatType];
      chatState.selectedCollaborationId = null;
      chatState.selectedRoomId = null;
    },

    setMessagesForRoom(
      state,
      action: PayloadAction<{
        collaborationId: string;
        roomId: string;
        messages: Message[];
        lastVisible?: any;
        hasMore?: boolean;
      }>
    ) {
      const { collaborationId, roomId, messages, lastVisible, hasMore } =
        action.payload;
      const key = `${collaborationId}_${roomId}`;
      state.messagesByRoom[key] = {
        messages,
        hasMore: hasMore ?? true,
        isLoading: false,
        lastVisible,
      };
    },

    // Tambah pesan lama (prepend)
    appendOlderMessages(
      state,
      action: PayloadAction<{
        collaborationId: string;
        roomId: string;
        messages: Message[];
        lastVisible?: any;
        hasMore?: boolean;
      }>
    ) {
      const { collaborationId, roomId, messages, lastVisible, hasMore } =
        action.payload;
      const key = `${collaborationId}_${roomId}`;
      const existing = state.messagesByRoom[key]?.messages || [];
      const newMessages = [...messages, ...existing];
      state.messagesByRoom[key] = {
        messages: uniqueById(newMessages),
        hasMore: hasMore ?? true,
        isLoading: false,
        lastVisible,
      };
    },

    // Tambah pesan baru (push)
    addNewMessage(
      state,
      action: PayloadAction<{
        collaborationId: string;
        roomId: string;
        message: Message;
      }>
    ) {
      const { collaborationId, roomId, message } = action.payload;
      const key = `${collaborationId}_${roomId}`;
      const existing = state.messagesByRoom[key]?.messages || [];
      state.messagesByRoom[key] = {
        ...state.messagesByRoom[key],
        messages: uniqueById([...existing, message]),
      };
    },

    // Set loading message
    setMessagesLoading(
      state,
      action: PayloadAction<{
        collaborationId: string;
        roomId: string;
        isLoading: boolean;
      }>
    ) {
      const { collaborationId, roomId, isLoading } = action.payload;
      const key = `${collaborationId}_${roomId}`;
      if (!state.messagesByRoom[key]) {
        state.messagesByRoom[key] = {
          messages: [],
          hasMore: true,
          isLoading,
        };
      } else {
        state.messagesByRoom[key].isLoading = isLoading;
      }
    },

    updateMessage(
      state,
      action: PayloadAction<{
        collaborationId: string;
        roomId: string;
        message: Message;
      }>
    ) {
      const { collaborationId, roomId, message } = action.payload;
      const key = `${collaborationId}_${roomId}`;
      const room = state.messagesByRoom[key];

      if (!room) return;

      const updatedMessages = room.messages.map((msg) =>
        msg.id === message.id ? { ...msg, ...message } : msg
      );

      state.messagesByRoom[key].messages = updatedMessages;
    },
  },
});

// Util: sorting helper
const sortCollaborationIdsByLastInteraction = (
  collabs: Record<string, Collaboration>,
  ids: string[]
): string[] => {
  return [...ids]
    .map((id) => collabs[id])
    .filter(Boolean)
    .sort(
      (a, b) =>
        (b.lastInteractionAt?.seconds ?? 0) -
        (a.lastInteractionAt?.seconds ?? 0)
    )
    .sort((a, b) => (b.unreadCount ?? 0) - (a.unreadCount ?? 0)) // Sort by unread count use descending order
    .map((c) => c.id);
};

// Util: calculate unread count
const calculateTotalUnread = (
  collaborations: Record<string, Collaboration>
): number => {
  return Object.values(collaborations).reduce(
    (sum, collab) => sum + (collab.unreadCount || 0),
    0
  );
};

const getTimestampSeconds = (value?: any): number | undefined => {
  if (!value) return undefined;
  if (typeof value.seconds === "number") return value.seconds;
  if (typeof value.toMillis === "function") return value.toMillis() / 1000;
  return undefined;
};

const isAfterTimestamp = (next?: any, current?: any): boolean => {
  const nextSeconds = getTimestampSeconds(next);
  const currentSeconds = getTimestampSeconds(current);
  return nextSeconds !== undefined && (currentSeconds === undefined || nextSeconds > currentSeconds);
};

const isBeforeTimestamp = (next?: any, current?: any): boolean => {
  const nextSeconds = getTimestampSeconds(next);
  const currentSeconds = getTimestampSeconds(current);
  return nextSeconds !== undefined && (currentSeconds === undefined || nextSeconds < currentSeconds);
};

// Util: helper unique by id
const uniqueById = (items: any[]) => {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (
      String(item?.messageType).toLowerCase() === "text" &&
      (!item?.textContent || item.textContent.trim() === "")
    ) {
      return false;
    }

    const id = item?.id;
    if (!id || seen.has(id)) {
      return false;
    }

    seen.add(id);
    return true;
  });
};

export const {
  setActiveTab,
  setFilter,
  setLoading,
  setHasMore,
  setChatListCursor,
  updateCollaboration,
  setCollaborations,
  appendCollaborations,
  removeCollaboration,
  setSelectedCollaboration,
  setSelectedRoom,
  setCollaborationRooms,
  resetRoomUnreadCount,
  clearSelectedChat,
  setMessagesForRoom,
  appendOlderMessages,
  addNewMessage,
  setMessagesLoading,
  updateMessage,
} = chatSlice.actions;

export default chatSlice.reducer;
